import React, { useEffect, useMemo, useState } from "react";
import { MainLayout } from "../layout/mainLayout";
import { PageTitle } from "../layout/pageTitle";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, TextField, Typography } from "@mui/material";
import { MuiDateTimePicker } from "../framework/muiDateTimePicker";
import { useAppDispatch, useAppSelector } from "../../framework/redux/hooks";
import { fetchVehicleMaintenanceById, saveVehicleMaintenanceThunk } from "../../framework/redux/slices/vehicleMaintenanceSlice";
import { addErrorMsg } from "../../framework/redux/slices/appSlice";
import { handleApiError } from "../../services/baseService";
import { VehicleMaintenanceDto } from "../../models/vehicleMaintenance/vehicleMaintenance";
import { isServiceEditableForDriver } from "./vehicleMaintenanceUtils";

type VehicleMaintenanceSliceShape = {
    items: VehicleMaintenanceDto[];
};

export const VehicleMaintenanceEdit = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { id } = useParams<{ id: string }>();
    const item: VehicleMaintenanceDto | undefined = useAppSelector(
        (s) => (s.vehicleMaintenance as VehicleMaintenanceSliceShape).items.find((m) => m.id === id)
    );

    const canEdit = useMemo(() => {
        if (!item) return false;
        return isServiceEditableForDriver(item);
    }, [item]);

    const [title, setTitle] = useState<string>("");
    const [maintenanceDate, setMaintenanceDate] = useState<string>("");
    const [price, setPrice] = useState<string>("");
    const [comment, setComment] = useState<string>("");
    const [driverComment, setDriverComment] = useState<string>("");

    useEffect(() => {
        if (id && !item) {
            dispatch(fetchVehicleMaintenanceById(id)).unwrap().catch(handleApiError);
        }
    }, [id, !!item]);

    useEffect(() => {
        if (!item) return;
        setTitle(item.title ?? "");
        setMaintenanceDate(item.maintenanceDate ?? "");
        setPrice(item.price === null || item.price === undefined ? "" : String(item.price));
        setComment(item.comment ?? "");
        setDriverComment(item.driverComment ?? "");
    }, [item?.id]);

    if (!id) {
        return null;
    }

    const handleSave = async () => {
        if (!item) return;

        const trimmedPrice = price.trim();
        const parsedPrice = trimmedPrice === "" ? undefined : Number(trimmedPrice.replace(",", "."));
        if (trimmedPrice !== "" && Number.isNaN(parsedPrice)) {
            dispatch(addErrorMsg({ message: t("maintenance.invalidPrice") }));
            return;
        }

        const payload: VehicleMaintenanceDto = {
            ...item,
            title: title || undefined,
            maintenanceDate: maintenanceDate || undefined,
            price: parsedPrice,
            comment: comment ?? "",
            driverComment: driverComment ?? "",
        };

        await dispatch(saveVehicleMaintenanceThunk(payload)).unwrap();
        navigate(`/services/${id}`, { replace: true });
    };

    return (
        <MainLayout topComponent={<PageTitle header={t("maintenance.editTitle")} />} hasPx>
            {!item ? (
                <Typography>{t("common.loading")}</Typography>
            ) : !canEdit ? (
                <Typography>{t("maintenance.editNotAllowed")}</Typography>
            ) : (
                <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                        label={t("maintenance.title")}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />

                    <MuiDateTimePicker
                        value={maintenanceDate}
                        onChange={(v) => setMaintenanceDate(v as string)}
                        dateLabel={t("maintenance.maintenanceDate")}
                        dateOnly={true}
                    />

                    <TextField
                        label={t("maintenance.price")}
                        type="text"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        inputProps={{ inputMode: "decimal" }}
                    />

                    <TextField
                        label={t("maintenance.comment")}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        multiline
                        minRows={3}
                    />

                    <TextField
                        label={t("maintenance.driverComment")}
                        value={driverComment}
                        onChange={(e) => setDriverComment(e.target.value)}
                        multiline
                        minRows={3}
                    />

                    <Box mt={1} display="flex" gap={2}>
                        <Button variant="contained" onClick={() => navigate(`/services/${id}`)}>
                            {t("common.cancel")}
                        </Button>
                        <Button variant="contained" onClick={() => void handleSave().catch(handleApiError)}>
                            {t("common.save")}
                        </Button>
                    </Box>
                </Box>
            )}
        </MainLayout>
    );
};
